import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOMeta from '@/components/feature/SEOMeta';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: '关于我们' },
  ];

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '草莓视频在线播放',
    url: 'https://www.caomei-sp.com',
    description: '草莓视频在线播放平台，提供海量高清视频资源，无需下载即可流畅观看。',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+86-400-888-6666',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于草莓视频在线播放',
    description: '了解草莓视频在线播放的品牌故事、核心优势以及联系方式。',
    url: 'https://www.caomei-sp.com/about',
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      { '@type': 'Question', name: '草莓视频在线播放的内容来源是什么？', acceptedAnswer: { '@type': 'Answer', text: '我们的内容来自正规版权合作方和用户自主上传（经审核），确保所有视频内容合法合规，为用户提供安全可靠的观影环境。' } },
      { '@type': 'Question', name: '如何联系草莓视频的客服团队？', acceptedAnswer: { '@type': 'Answer', text: '您可以通过电子邮件（kefu@caomei-sp.com）或客服热线（400-888-6666）联系我们的客服团队，工作时间为每天9:00-21:00。' } },
    ],
  };

  const testimonials = [
    {
      name: '张先生',
      role: '资深影迷',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20Asian%20man%20in%20casual%20attire%2C%20friendly%20smile%2C%20simple%20clean%20background%2C%20portrait%20photography%2C%20warm%20natural%20lighting&width=100&height=100&seq=avatar-01&orientation=squarish',
      text: '草莓视频的画质真的没话说，4K超清播放非常流畅。界面简洁好用，分类也很齐全，已经成了我每天的必备应用。',
      rating: 5,
    },
    {
      name: '李女士',
      role: '全职妈妈',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20Asian%20woman%20with%20warm%20smile%2C%20simple%20clean%20background%2C%20portrait%20photography%2C%20soft%20natural%20lighting&width=100&height=100&seq=avatar-02&orientation=squarish',
      text: '少儿频道内容很丰富，孩子特别喜欢看。而且没有乱七八糟的广告，家长很放心。强烈推荐给有娃的家庭。',
      rating: 5,
    },
    {
      name: '王同学',
      role: '大学生',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20young%20Asian%20man%20with%20glasses%2C%20friendly%20expression%2C%20simple%20clean%20background%2C%20portrait%20photography%2C%20warm%20natural%20lighting&width=100&height=100&seq=avatar-03&orientation=squarish',
      text: '纪录片的资源太赞了！很多BBC和国产的精品纪录片都能在这里找到。用来学习课外知识特别方便，同学们都在用。',
      rating: 5,
    },
  ];

  return (
    <>
      <SEOMeta
        title="关于我们 - 草莓视频在线播放 | 品牌介绍与联系方式"
        description="了解草莓视频在线播放的品牌故事、核心优势、用户口碑评价以及联系方式。草莓视频在线播放,草莓视频高清在线播放,草莓视频免费在线观看,在线视频播放平台。"
        keywords="草莓视频在线播放,草莓视频关于我们,草莓视频品牌介绍,草莓视频联系我们,在线视频播放平台"
        canonicalUrl="https://www.caomei-sp.com/about"
        ogTitle="关于我们 - 草莓视频在线播放"
        ogDescription="了解草莓视频在线播放的品牌故事、核心优势以及联系方式。"
        ogUrl="https://www.caomei-sp.com/about"
        jsonLd={[organizationSchema, webPageSchema, faqSchema]}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        {/* Page Header */}
        <div className="bg-background-100 py-8 md:py-10">
          <div className="w-full px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />
            <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mt-2">
              关于草莓视频
            </h1>
            <p className="text-sm text-foreground-500 mt-1">
              高清流畅 · 海量资源 · 多端兼容
            </p>
          </div>
        </div>

        {/* Brand Introduction */}
        <section className="py-12 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20cozy%20home%20cinema%20room%20with%20large%20screen%20and%20comfortable%20seating%2C%20warm%20ambient%20coral%20lighting%2C%20minimalist%20interior%20design%2C%20premium%20entertainment%20setup%2C%20atmospheric%20cinematic%20mood%2C%20professional%20interior%20photography&width=600&height=400&seq=about-brand-intro&orientation=landscape"
                  alt="草莓视频在线播放 - 品牌介绍"
                  className="w-full rounded-lg"
                />
              </div>
              <div>
                <h2 className="text-xl font-heading text-foreground-900 mb-4">品牌故事</h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  草莓视频在线播放成立于2020年，致力于为广大用户提供高品质的在线视频观看体验。
                  我们深知在这个快节奏的时代，人们需要一个简单、流畅、内容丰富的视频平台来放松身心。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  从最初的几百部视频资源起步，经过团队不懈努力，草莓视频如今已发展成为拥有超过10万部高清视频的综合在线播放平台。
                  涵盖电影、电视剧、动漫、综艺、纪录片、音乐、游戏、体育、少儿等丰富内容板块。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  我们的使命是让每一个人都能轻松享受高品质的视听娱乐体验。无论您使用什么设备，
                  无论您在什么地方，打开草莓视频在线播放，精彩内容即刻呈现。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Core Advantages */}
        <section className="py-12 md:py-14 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <h2 className="text-xl font-heading text-foreground-900 text-center mb-10">核心优势</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {[
                {
                  icon: 'ri-4k-line',
                  title: '超清画质体验',
                  desc: '全面支持480P至4K超高清画质，智能码率自适应技术确保不同网络环境下都能获得最佳观看体验。HDR高动态范围技术让色彩更加真实鲜艳。',
                },
                {
                  icon: 'ri-speed-up-line',
                  title: '极致流畅播放',
                  desc: '自建全球CDN加速节点，毫秒级加载响应。智能预加载技术让视频切换无缝衔接，告别卡顿和缓冲等待的烦恼。',
                },
                {
                  icon: 'ri-stack-line',
                  title: '海量资源库',
                  desc: '超过10万部高清视频资源，每日更新最新内容。从经典老片到热门新作，从国产精品到海外佳作，满足各类观影需求。',
                },
                {
                  icon: 'ri-smartphone-line',
                  title: '全端无缝体验',
                  desc: '支持PC、手机、平板等多端同步观看。智能记忆播放进度，跨设备续播不中断。界面自适应不同屏幕尺寸，操作始终顺手。',
                },
                {
                  icon: 'ri-shield-check-line',
                  title: '安全绿色环境',
                  desc: '严格的内容审核机制，确保所有视频内容合法合规。无恶意弹窗，无诱导下载，为全年龄段用户提供安全放心的观影环境。',
                },
                {
                  icon: 'ri-user-heart-line',
                  title: '用户至上服务',
                  desc: '7×24小时客服支持，快速响应用户反馈。持续优化产品体验，认真倾听每一位用户的声音，让观影成为一种享受。',
                },
              ].map((item) => (
                <div key={item.title} className="flex gap-4 p-5 bg-background-50 rounded-lg">
                  <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center rounded-xl bg-primary-100">
                    <i className={`${item.icon} text-xl text-primary-500`}></i>
                  </div>
                  <div>
                    <h3 className="font-heading text-base text-foreground-900 mb-1.5">{item.title}</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* User Reputation - id for anchor */}
        <section id="reputation" className="py-12 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <h2 className="text-xl font-heading text-foreground-900 text-center mb-2">用户口碑</h2>
            <p className="text-sm text-foreground-500 text-center mb-10">来自真实用户的使用评价</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {testimonials.map((t) => (
                <div key={t.name} className="bg-background-100 rounded-lg p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-12 h-12 rounded-full object-cover"
                      loading="lazy"
                    />
                    <div>
                      <div className="text-sm font-medium text-foreground-900">{t.name}</div>
                      <div className="text-xs text-foreground-500">{t.role}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <div key={i} className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-accent-500 text-sm"></i>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.text}</p>
                </div>
              ))}
            </div>

            <div className="mt-10 text-center bg-primary-100 rounded-lg px-6 py-8">
              <div className="text-3xl font-heading text-primary-500 mb-2">98%</div>
              <p className="text-sm text-foreground-600">的用户表示会向朋友推荐草莓视频在线播放</p>
            </div>
          </div>
        </section>

        {/* Contact Info - id for anchor */}
        <section id="contact" className="py-12 md:py-14 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <h2 className="text-xl font-heading text-foreground-900 text-center mb-2">联系我们</h2>
            <p className="text-sm text-foreground-500 text-center mb-10">我们期待听到您的声音</p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
              <div className="bg-background-50 rounded-lg p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-xl bg-primary-100">
                  <i className="ri-mail-line text-xl text-primary-500"></i>
                </div>
                <h3 className="text-sm font-medium text-foreground-900 mb-1">电子邮件</h3>
                <p className="text-sm text-foreground-500">kefu@caomei-sp.com</p>
              </div>
              <div className="bg-background-50 rounded-lg p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-xl bg-primary-100">
                  <i className="ri-phone-line text-xl text-primary-500"></i>
                </div>
                <h3 className="text-sm font-medium text-foreground-900 mb-1">客服热线</h3>
                <p className="text-sm text-foreground-500">400-888-6666</p>
              </div>
              <div className="bg-background-50 rounded-lg p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-xl bg-primary-100">
                  <i className="ri-time-line text-xl text-primary-500"></i>
                </div>
                <h3 className="text-sm font-medium text-foreground-900 mb-1">工作时间</h3>
                <p className="text-sm text-foreground-500">每天 9:00 - 21:00</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}